import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Send, User, Mail, Building, MessageSquare, CheckCircle, AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';

// Simple math captcha generator
const generateCaptcha = () => {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  const operators = ['+', '-', '×'];
  const operator = operators[Math.floor(Math.random() * 3)];
  
  let answer;
  switch (operator) {
    case '+':
      answer = num1 + num2;
      break;
    case '-':
      answer = Math.max(num1, num2) - Math.min(num1, num2);
      return { 
        question: `${Math.max(num1, num2)} ${operator} ${Math.min(num1, num2)}`, 
        answer 
      };
    case '×':
      answer = num1 * num2;
      break;
    default:
      answer = num1 + num2;
  }
  
  return { question: `${num1} ${operator} ${num2}`, answer };
};

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: 'investor',
    message: '',
    gdprConsent: false
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ question: '', answer: 0 });
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    setCaptchaError(false);
  }, []);

  useEffect(() => {
    refreshCaptcha();
  }, [refreshCaptcha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    setCaptchaError(false);

    // Verify captcha
    if (parseInt(captchaInput) !== captcha.answer) {
      setCaptchaError(true);
      setIsSubmitting(false);
      setStatus({
        type: 'error',
        message: t(
          'Incorrect answer. Please try again.',
          'Respuesta incorrecta. Por favor, inténtalo de nuevo.'
        )
      });
      refreshCaptcha();
      return;
    }

    try {
      const response = await fetch('/contacto.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, form: 'contact', website: '' })
      });
      const result = await response.json().catch(() => ({ ok: false }));
      if (!response.ok || !result.ok) {
        throw new Error((result && result.error) || 'Server error');
      }
      
      setStatus({
        type: 'success',
        message: t(
          'Thank you for your message. We will contact you shortly.',
          'Gracias por tu mensaje. Nos pondremos en contacto contigo pronto.'
        )
      });
      setFormData({ name: '', email: '', company: '', interest: 'investor', message: '', gdprConsent: false });
      refreshCaptcha();
    } catch (error) {
      setStatus({
        type: 'error',
        message: t(
          'There was an error sending your message. Please try again.',
          'Hubo un error al enviar tu mensaje. Por favor, inténtalo de nuevo.'
        )
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    { value: 'investor', label: t('Investment Opportunities', 'Oportunidades de Inversión') },
    { value: 'grant', label: t('Grant Collaboration', 'Colaboración en Subvenciones') },
    { value: 'partner', label: t('Strategic Partnership', 'Alianza Estratégica') },
    { value: 'research', label: t('Research Collaboration', 'Colaboración en Investigación') },
    { value: 'media', label: t('Media / Press', 'Medios / Prensa') },
    { value: 'other', label: t('Other', 'Otro') }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-[#1F4E78] to-[#2E5090]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('Contact Us', 'Contáctanos')}
          </h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            {t(
              'Interested in learning more about Metasigna? We would love to hear from you.',
              '¿Interesado en conocer más sobre Metasigna? Nos encantaría saber de ti.'
            )}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-[#1F4E78] mb-2">
                {t('Get in Touch', 'Ponte en Contacto')}
              </h2>
              <p className="text-gray-600">
                {t(
                  'Fill out the form below and our team will respond within 48 hours.',
                  'Completa el formulario y nuestro equipo responderá en 48 horas.'
                )}
              </p>
            </div>

            {/* Status Message */}
            {status.message && (
              <div
                data-testid="form-status-message"
                className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                  status.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {status.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{status.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-[#1F4E78]" />
                    <span>{t('Full Name', 'Nombre Completo')} *</span>
                  </div>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E5090] focus:border-transparent transition-all outline-none"
                  placeholder={t('Enter your full name', 'Ingresa tu nombre completo')}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-[#1F4E78]" />
                    <span>{t('Email Address', 'Correo Electrónico')} *</span>
                  </div>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="contact-email-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E5090] focus:border-transparent transition-all outline-none"
                  placeholder={t('Enter your email', 'Ingresa tu correo')}
                />
              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-[#1F4E78]" />
                    <span>{t('Company / Organization', 'Empresa / Organización')}</span>
                  </div>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  data-testid="contact-company-input"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E5090] focus:border-transparent transition-all outline-none"
                  placeholder={t('Enter your company name', 'Ingresa el nombre de tu empresa')}
                />
              </div>

              {/* Interest */}
              <div>
                <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Area of Interest', 'Área de Interés')} *
                </label>
                <select
                  id="interest"
                  name="interest"
                  data-testid="contact-interest-select"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E5090] focus:border-transparent transition-all outline-none bg-white"
                >
                  {interestOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4 text-[#1F4E78]" />
                    <span>{t('Message', 'Mensaje')} *</span>
                  </div>
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-textarea"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E5090] focus:border-transparent transition-all outline-none resize-none"
                  placeholder={t(
                    'Tell us how we can help you...',
                    'Cuéntanos cómo podemos ayudarte...'
                  )}
                />
              </div>

              {/* Captcha */}
              <div className={`p-4 rounded-lg border-2 ${captchaError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <div className="flex items-center space-x-2">
                    <ShieldCheck className="w-4 h-4 text-[#1F4E78]" />
                    <span>{t('Security Verification', 'Verificación de Seguridad')} *</span>
                  </div>
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3 bg-white px-4 py-3 rounded-lg border border-gray-300 shadow-sm">
                    <span className="text-lg font-mono font-bold text-[#1F4E78]">
                      {captcha.question} =
                    </span>
                  </div>
                  <input
                    type="number"
                    data-testid="captcha-input"
                    value={captchaInput}
                    onChange={(e) => {
                      setCaptchaInput(e.target.value);
                      setCaptchaError(false);
                    }}
                    required
                    className={`w-24 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2E5090] focus:border-transparent transition-all outline-none text-center font-bold ${
                      captchaError ? 'border-red-400' : 'border-gray-300'
                    }`}
                    placeholder="?"
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    data-testid="captcha-refresh-button"
                    className="p-3 text-gray-500 hover:text-[#1F4E78] hover:bg-white rounded-lg transition-colors"
                    title={t('New question', 'Nueva pregunta')}
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('Solve the math problem to verify you are human.', 'Resuelve el problema matemático para verificar que eres humano.')}
                </p>
              </div>

              {/* GDPR Consent Checkbox */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="gdprConsent"
                  name="gdprConsent"
                  data-testid="gdpr-consent-checkbox"
                  checked={formData.gdprConsent}
                  onChange={(e) => setFormData(prev => ({ ...prev, gdprConsent: e.target.checked }))}
                  required
                  className="mt-1 w-4 h-4 text-[#1F4E78] border-gray-300 rounded focus:ring-[#2E5090]"
                />
                <label htmlFor="gdprConsent" className="text-sm text-gray-600">
                  {t(
                    'I consent to Metasigna storing and processing my personal information in accordance with the Privacy Policy. My data will be used solely to respond to my inquiry and will not be shared with third parties. *',
                    'Consiento que Metasigna almacene y procese mi información personal de acuerdo con la Política de Privacidad. Mis datos se utilizarán únicamente para responder a mi consulta y no se compartirán con terceros. *'
                  )}
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-[#1F4E78] to-[#2E5090] text-white font-semibold rounded-lg hover:from-[#2E5090] hover:to-[#1F4E78] transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {isSubmitting ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('Sending...', 'Enviando...')}</span>
                  </span>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('Send Message', 'Enviar Mensaje')}</span>
                  </>
                )}
              </button>
            </form>

            {/* Privacy Note */}
            <p className="mt-6 text-xs text-gray-500 text-center">
              {t(
                'By submitting this form, you agree to our privacy policy. We will never share your information with third parties.',
                'Al enviar este formulario, aceptas nuestra política de privacidad. Nunca compartiremos tu información con terceros.'
              )}
            </p>
          </div>

          {/* Additional Contact Info */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 text-center">
              <div className="w-12 h-12 bg-[#1F4E78]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-[#1F4E78]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('Email', 'Correo')}</h3>
              <p className="text-[#2E5090]">contact@metasigna.company</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 text-center">
              <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-6 h-6 text-[#1F4E78]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('Address', 'Dirección')}</h3>
              <p className="text-gray-600">Harju maakond, Tallinn</p>
              <p className="text-gray-600">Kesklinna linnaosa</p>
              <p className="text-gray-600">Järvevana tee 9, 11314</p>
              <p className="text-gray-600">Estonia</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
